import { colorPalette } from '@/ui/theme/colorPalette';
import { customShadows } from '@/ui/theme/customShadows';
import { createStyleOverrides } from '@/ui/theme/styleOverrides';
import { typography } from '@/ui/theme/typography';
import {
  createTheme,
  darken,
  lighten,
  Shadows,
  Theme,
} from '@mui/material/styles';
import type { CSSProperties } from 'react';

declare module '@mui/material/styles/createTypography' {
  interface FontStyle {
    fontWeightSemiBold: CSSProperties['fontWeight'];
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    customShadows: typeof customShadows;
  }
  interface ThemeOptions {
    customShadows?: typeof customShadows;
  }
  // Extend PaletteColor to include lighter and darker properties
  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }
  // Extend MUI's existing Background interface to add neutral
  interface TypeBackground {
    neutral?: string;
  }
  // Extend MUI's existing Action interface to add disabledBackground
  // Note: MUI may already have this, so we ensure it matches the palette structure
  interface TypeAction {
    disabledBackground: string;
  }
  interface Palette {
    hotel: PaletteColor;
    white: {
      main: string;
    };
    black: {
      main: string;
    };
  }
  interface PaletteOptions {
    hotel?: SimplePaletteColorOptions;
    white?: {
      main?: string;
    };
    black?: {
      main?: string;
    };
  }
  interface BreakpointOverrides {
    xxl: true;
  }
}

const spacing = 8;

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
    /** Wide desktop — four-up grids, extra horizontal layout */
    xxl: 1920,
  },
};

const createShadowsArray = (): Shadows => {
  const customShadows = [
    'none',
    '0px 1px 4px rgba(4, 0, 89, 0.08)',
    '0px 2px 8px rgba(4, 0, 89, 0.12)',
    '0px 4px 16px rgba(4, 0, 89, 0.16)',
    '0 0 2px 0 rgba(145, 158, 171, 0.20)',
    '0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  ];
  const result = [...customShadows];
  while (result.length < 25) {
    result.push('none');
  }
  return result as Shadows;
};

const shadows = createShadowsArray();

// True only for colors MUI can parse — i.e. the colors createHubThemeLight actually
// applies rather than falling back to the brand default. `darken` parses the color
// (via decomposeColor) and throws on anything unparseable; the coefficient is
// irrelevant here, we only care whether it throws.
export function isValidThemeColor(color?: string): color is string {
  if (!color) return false;
  try {
    darken(color, 0);
    return true;
  } catch {
    return false;
  }
}

// Buttons, links and badges in this theme are painted from primary.dark/darker
// (see styleOverrides), so a custom org color must regenerate the whole primary
// scale — overriding only `main` would leave those shades at the brand defaults.
// Falls back to the brand palette if the stored color is not a valid CSS color.
function buildPrimaryScale(primaryColor?: string): typeof colorPalette.primary {
  if (!isValidThemeColor(primaryColor)) return colorPalette.primary;
  return {
    lighter: lighten(primaryColor, 0.85),
    light: lighten(primaryColor, 0.5),
    main: primaryColor,
    dark: darken(primaryColor, 0.35),
    darker: darken(primaryColor, 0.55),
    contrastText: colorPalette.primary.contrastText,
  };
}

export function createHubThemeLight(primaryColor?: string): Theme {
  // Destructure divider to extract main, and keep the rest
  const { divider, ...restPalette } = colorPalette;

  const palette = {
    ...restPalette,
    primary: buildPrimaryScale(primaryColor),
    // MUI expects divider to be a string, not an object
    divider: divider.main,
  };

  return createTheme({
    typography,
    spacing,
    breakpoints,
    palette,
    shadows,
    customShadows: customShadows,
    components: createStyleOverrides(palette, shadows),
  });
}

// In case we ever want to add Dark mode.
export const hubTheme = createHubThemeLight();
export const hubThemeLight = hubTheme;
